<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="form-page">
    <div class="form-page-header">
      <div class="form-page-header__icon"><i class="pi pi-user-plus"></i></div>
      <div class="form-page-header__text">
        <h1 class="form-page-header__title">إضافة حجز {{ grade?.name ?? '' }}</h1>
        <p class="form-page-header__subtitle">تسجيل بيانات طالب جديد في الفصل الدراسي</p>
      </div>
    </div>

    <div class="container-fluid">
      <div v-if="loadingSettings" class="text-center py-5">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
      </div>

      <form v-else @submit.prevent="sendForm">

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-id-card"></i></div>
            <span class="form-card__title">البيانات الشخصية</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">اسم الطالب <span class="required">*</span></label>
                  <InputText v-model="student_name" placeholder="أدخل الاسم كاملاً" class="w-100" @input="v$.student_name.$touch()" />
                  <span class="field-error" v-if="v$.student_name.$error">
                    <i class="pi pi-exclamation-circle"></i>
                    <template v-if="v$.student_name.required.$invalid">برجاء ادخال اسم الطالب</template>
                    <template v-else-if="v$.student_name.arabicOnly.$invalid">يرجى إدخال اسم الطالب باللغة العربية فقط.</template>
                    <template v-else>يجب ألا يقل اسم الطالب عن 10 أحرف</template>
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">رقم هاتف الطالب <span class="required">*</span></label>
                  <InputText v-model="student_phone" inputmode="tel" placeholder="01XXXXXXXXX" dir="ltr" class="w-100" @keypress="onlyNumbers" @input="v$.student_phone.$touch()" />
                  <span class="field-error" v-if="v$.student_phone.$error">
                    <i class="pi pi-exclamation-circle"></i>
                    <template v-if="v$.student_phone.required.$invalid">برجاء ادخال رقم هاتف الطالب</template>
                    <template v-else-if="v$.student_phone.exact11.$invalid">رقم الهاتف يجب أن يتكون من 11 رقم.</template>
                    <template v-else>يجب أن يبدأ الرقم بـ 01.</template>
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">رقم هاتف ولي الأمر <span class="optional">اختياري</span></label>
                  <InputText v-model="parent_phone" inputmode="tel" placeholder="01XXXXXXXXX" dir="ltr" class="w-100" @keypress="onlyNumbers" @input="v$.parent_phone.$touch()" />
                  <span class="field-error" v-if="v$.parent_phone.$error">
                    <i class="pi pi-exclamation-circle"></i> رقم ولي الأمر يجب أن يتكون من 7 إلى 11 رقم.
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">الجنس <span class="required">*</span></label>
                  <Dropdown v-model="selected_gender" :options="genders" showClear optionLabel="name" placeholder="اختر الجنس" class="w-100" />
                  <span class="field-error" v-if="v$.selected_gender.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء اختيار الجنس
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">نظام التعليم <span class="optional">اختياري</span></label>
                  <Dropdown v-model="selected_type" :options="types" showClear optionLabel="name" placeholder="اختر نظام التعليم" class="w-100" />
                </div>
              </div>

              <div class="col-12">
                <div class="form-field">
                  <label class="field-label">وضع الدراسة <span class="required">*</span></label>
                  <div class="type-selector">
                    <label
                      v-for="option in studyModeOptions"
                      :key="option.value"
                      :class="['type-selector__item', { active: selected_study_mode?.value === option.value }]"
                    >
                      <input type="radio" v-model="selected_study_mode" :value="option" class="d-none" />
                      <i :class="`pi ${option.icon}`"></i>
                      {{ option.label }}
                    </label>
                  </div>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12" v-if="formSettings.student_type.visible">
                <div class="form-field">
                  <label class="field-label">
                    {{ formSettings.student_type.label }}
                    <span class="required" v-if="formSettings.student_type.required">*</span>
                    <span class="optional" v-else>اختياري</span>
                  </label>
                  <Dropdown v-model="selected_student_type" :options="studentTypes" showClear optionLabel="name" placeholder="اختر نوع الطالب" class="w-100" />
                  <span class="field-error" v-if="v$.selected_student_type.$error">
                    <i class="pi pi-exclamation-circle"></i> {{ formSettings.student_type.error_required }}
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12" v-if="formSettings.whatsapp_number.visible">
                <div class="form-field">
                  <label class="field-label">
                    {{ formSettings.whatsapp_number.label }}
                    <span class="required" v-if="formSettings.whatsapp_number.required">*</span>
                    <span class="optional" v-else>اختياري</span>
                  </label>
                  <InputText v-model="whatsapp_number" inputmode="tel" :placeholder="formSettings.whatsapp_number.placeholder" dir="ltr" class="w-100" @keypress="onlyNumbers" @input="v$.whatsapp_number.$touch()" />
                  <span class="field-error" v-if="v$.whatsapp_number.$error">
                    <i class="pi pi-exclamation-circle"></i>
                    <template v-if="v$.whatsapp_number.required?.$invalid">{{ formSettings.whatsapp_number.error_required }}</template>
                    <template v-else-if="v$.whatsapp_number.exact11.$invalid">{{ formSettings.whatsapp_number.error_digits }}</template>
                    <template v-else>{{ formSettings.whatsapp_number.error_prefix }}</template>
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12" v-if="formSettings.address.visible">
                <div class="form-field">
                  <label class="field-label">
                    {{ formSettings.address.label }}
                    <span class="required" v-if="formSettings.address.required">*</span>
                    <span class="optional" v-else>اختياري</span>
                  </label>
                  <InputText v-model="address" :placeholder="formSettings.address.placeholder" class="w-100" />
                  <span class="field-error" v-if="v$.address.$error">
                    <i class="pi pi-exclamation-circle"></i> {{ formSettings.address.error_required }}
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12" v-if="formSettings.student_actual_grade.visible">
                <div class="form-field">
                  <label class="field-label">
                    {{ formSettings.student_actual_grade.label }}
                    <span class="required" v-if="formSettings.student_actual_grade.required">*</span>
                    <span class="optional" v-else>اختياري</span>
                  </label>
                  <InputText v-model="student_actual_grade" inputmode="numeric" :placeholder="formSettings.student_actual_grade.placeholder" dir="ltr" class="w-100" @keypress="onlyNumbers" @input="v$.student_actual_grade.$touch()" />
                  <span class="field-error" v-if="v$.student_actual_grade.$error">
                    <i class="pi pi-exclamation-circle"></i>
                    <template v-if="v$.student_actual_grade.required?.$invalid">{{ formSettings.student_actual_grade.error_required }}</template>
                    <template v-else-if="v$.student_actual_grade.numeric.$invalid">{{ formSettings.student_actual_grade.error_invalid }}</template>
                    <template v-else-if="v$.student_actual_grade.minValue.$invalid">{{ formSettings.student_actual_grade.error_min }}</template>
                    <template v-else-if="v$.student_actual_grade.notGreaterThanMaximum.$invalid">{{ formSettings.student_actual_grade.error_range }}</template>
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12" v-if="formSettings.maximum_grade.visible">
                <div class="form-field">
                  <label class="field-label">
                    {{ formSettings.maximum_grade.label }}
                    <span class="required" v-if="formSettings.maximum_grade.required">*</span>
                    <span class="optional" v-else>اختياري</span>
                  </label>
                  <InputText v-model="maximum_grade" inputmode="numeric" :placeholder="formSettings.maximum_grade.placeholder" dir="ltr" class="w-100" @keypress="onlyNumbers" @input="v$.maximum_grade.$touch()" />
                  <span class="field-error" v-if="v$.maximum_grade.$error">
                    <i class="pi pi-exclamation-circle"></i>
                    <template v-if="v$.maximum_grade.required?.$invalid">{{ formSettings.maximum_grade.error_required }}</template>
                    <template v-else-if="v$.maximum_grade.numeric.$invalid">{{ formSettings.maximum_grade.error_invalid }}</template>
                    <template v-else-if="v$.maximum_grade.minValue.$invalid">{{ formSettings.maximum_grade.error_min }}</template>
                    <template v-else-if="v$.maximum_grade.maxValue.$invalid">{{ formSettings.maximum_grade.error_max }}</template>
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>

        <div class="form-card" v-if="formSettings.certificate_image.visible">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-file-import"></i></div>
            <span class="form-card__title">{{ formSettings.certificate_image.label }}</span>
          </div>
          <div class="form-card__body">
            <label class="field-label">
              رفع الشهادة
              <span class="required" v-if="formSettings.certificate_image.required">*</span>
              <span class="optional" v-else>اختياري</span>
            </label>
            <div :class="['upload-zone', { 'upload-zone--has-file': imageEntry }]">
              <div class="upload-zone__icon">
                <i :class="`pi ${imageEntry ? 'pi-check' : 'pi-cloud-upload'}`"></i>
              </div>
              <p class="upload-zone__label">{{ imageEntry ? fileData.name : formSettings.certificate_image.upload_text }}</p>
              <p class="upload-zone__hint" v-if="!imageEntry">{{ formSettings.certificate_image.upload_hint }}</p>
              <input type="file" @change="onFileChange" accept="image/png,image/gif,image/jpeg,image/jpg" />
            </div>
            <span class="field-error" v-if="v$.fileData.$error">
              <i class="pi pi-exclamation-circle"></i> {{ formSettings.certificate_image.error_required }}
            </span>
          </div>
        </div>

        <div class="notice-box notice-box--warning">
          <i class="pi pi-whatsapp" style="font-size:1.25rem;"></i>
          <div>
            <strong>هل واجهتك مشكلة في الحجز؟</strong>
            <p class="mb-0 mt-1" style="font-size:0.875rem;">
              تواصل معنا على الواتساب:
              <a href="https://wa.me/+201111488849" target="_blank" style="font-weight:700; margin-inline:4px;">01111488849</a>
            </p>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn-submit" :disabled="formSending">
            <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
            <i class="pi pi-check" v-else></i>
            {{ formSending ? 'جاري الإرسال...' : 'تأكيد الحجز' }}
          </button>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import InputText from 'primevue/inputtext';
import Swal from 'sweetalert2';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import Dropdown from 'primevue/dropdown';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';
import { scrollToFirstError } from '@/composables/useScrollToFirstError';
import {
  DEFAULT_FORM_SETTINGS,
  arabicOnly,
  digitsBetween,
  exactDigits,
  isNumericOrEmpty,
  maxValueOrEmpty,
  minValueOrEmpty,
  notGreaterThan,
  startsWithZeroOne,
  toEnglishDigits,
  trimmedMinLength,
} from '@/composables/useRegistrationValidators';

const STUDY_MODE_OPTIONS = [
  { label: 'حضوري فقط', value: 'offline', icon: 'pi-building' },
  { label: 'أونلاين فقط', value: 'online', icon: 'pi-wifi' },
  { label: 'أونلاين + حضوري', value: 'hybrid', icon: 'pi-sync' },
];

export default {
  name: 'AddGradeStudent',
  components: { InputText, Dropdown, Breadcrumb },

  data() {
    return {
      v$: useVuelidate(),
      student_name: '',
      student_phone: null,
      parent_phone: null,
      address: '',
      whatsapp_number: null,
      student_actual_grade: null,
      maximum_grade: null,
      imageEntry: '',
      fileData: '',
      formSending: false,
      loadingSettings: true,
      formSettings: DEFAULT_FORM_SETTINGS,
      selected_gender: null,
      selected_type: null,
      selected_student_type: null,
      selected_study_mode: STUDY_MODE_OPTIONS[0],
      studyModeOptions: STUDY_MODE_OPTIONS,
      genders: [
        { name: 'ولد', id: 1 },
        { name: 'بنت', id: 2 },
      ],
      types: [
        { name: 'عام', id: 1 },
        { name: 'أزهر', id: 2 },
      ],
      studentTypes: [
        { name: 'مستجد', id: 1 },
        { name: 'قديم', id: 2 },
      ],
    };
  },

  validations() {
    const fs = this.formSettings;
    return {
      student_name: { required, arabicOnly, minLength10: trimmedMinLength(10) },
      student_phone: { required, exact11: exactDigits(11), startsWith01: startsWithZeroOne },
      parent_phone: { digitsBetween: digitsBetween(7, 11) },
      selected_gender: { required },
      address: fs.address.required ? { required } : {},
      whatsapp_number: {
        ...(fs.whatsapp_number.required ? { required } : {}),
        exact11: exactDigits(fs.whatsapp_number.validation?.length ?? 11),
        startsWith01: startsWithZeroOne,
      },
      student_actual_grade: {
        ...(fs.student_actual_grade.required ? { required } : {}),
        numeric: isNumericOrEmpty,
        minValue: minValueOrEmpty(0),
        notGreaterThanMaximum: notGreaterThan(() => this.maximum_grade),
      },
      maximum_grade: {
        ...(fs.maximum_grade.required ? { required } : {}),
        numeric: isNumericOrEmpty,
        minValue: minValueOrEmpty(1),
        maxValue: maxValueOrEmpty(120),
      },
      selected_student_type: fs.student_type.required ? { required } : {},
      fileData: fs.certificate_image.required ? { required } : {},
    };
  },

  computed: {
    gradeStore() { return useGradeStore(); },
    gradeSlug() { return this.$route.params.gradeSlug; },
    grade() {
      return this.gradeStore.activeGrades.find(g => g.slug === this.gradeSlug)
          || this.gradeStore.allGrades.find(g => g.slug === this.gradeSlug)
          || null;
    },
    breadcrumbs() {
      return [
        { name: 'الرئيسية', path: '/' },
        { name: this.grade?.name ?? 'المرحلة', path: `/grades/${this.gradeSlug}/students` },
        { name: 'إضافة حجز', path: `/grades/${this.gradeSlug}/students/add` },
      ];
    },
  },

  methods: {
    onFileChange(e) {
      this.fileData = e.target.files[0];
      const reader = new FileReader();
      reader.readAsDataURL(this.fileData);
      reader.onload = () => (this.imageEntry = reader.result);
    },

    onlyNumbers(e) {
      const char = String.fromCharCode(e.which);
      if (!/[0-9٠-٩]/.test(char)) { e.preventDefault(); return; }
      const digits = (e.target.value.match(/[0-9٠-٩]/g) || []).length;
      if (digits >= 11) e.preventDefault();
    },

    toEnglishDigits(str) {
      return toEnglishDigits(str);
    },

    async fetchFormSettings() {
      if (!this.grade?.id) {
        this.formSettings = DEFAULT_FORM_SETTINGS;
        return;
      }
      try {
        const { data } = await axios.get(`/academic-years/${this.grade.id}/registration-settings`);
        this.formSettings = data.form_settings;
      } catch {
        this.formSettings = DEFAULT_FORM_SETTINGS;
      }
    },

    async sendForm() {
      this.v$.$validate();
      if (this.v$.$error) {
        this.$nextTick(() => scrollToFirstError());
        return;
      }

      const fd = new FormData();
      fd.append('student_name', this.student_name);
      fd.append('student_phone', this.toEnglishDigits(this.student_phone));
      if (this.parent_phone) fd.append('parent_phone', this.toEnglishDigits(this.parent_phone));
      fd.append('gender', this.selected_gender.id);
      if (this.selected_type) fd.append('education_type', this.selected_type.id);
      fd.append('study_mode', this.selected_study_mode?.value || 'offline');

      if (this.grade?.id) fd.append('academic_year_id', this.grade.id);

      if (this.formSettings.address.visible && this.address) fd.append('address', this.address);
      if (this.formSettings.whatsapp_number.visible && this.whatsapp_number) {
        fd.append('whatsapp_number', this.toEnglishDigits(this.whatsapp_number));
      }
      if (this.formSettings.student_actual_grade.visible && this.student_actual_grade) {
        fd.append('student_actual_grade', this.toEnglishDigits(String(this.student_actual_grade).replace(/,/g, '.')));
      }
      if (this.formSettings.maximum_grade.visible && this.maximum_grade) {
        fd.append('maximum_grade', this.toEnglishDigits(String(this.maximum_grade).replace(/,/g, '.')));
      }
      if (this.formSettings.student_type.visible && this.selected_student_type) {
        fd.append('student_type', this.selected_student_type.id);
      }
      if (this.formSettings.certificate_image.visible && this.fileData instanceof File) {
        fd.append('certificate_image', this.fileData);
      }

      this.formSending = true;
      try {
        const response = await axios.post('/register_dashboard', fd);
        this.resetForm();
        Swal.fire({
          position: 'center',
          icon: 'success',
          title: 'رقم الهاتف: ' + response.data.data.phone,
          text: 'برجاء تذكر رقم الهاتف لمعرفة حالة الحجز',
        });
      } catch (err) {
        Swal.fire({ position: 'center', icon: 'error', title: err.response?.data?.message ?? 'حدث خطأ' });
      } finally {
        this.formSending = false;
      }
    },

    resetForm() {
      this.v$.$reset();
      this.student_name = '';
      this.student_phone = null;
      this.parent_phone = null;
      this.address = '';
      this.whatsapp_number = null;
      this.student_actual_grade = null;
      this.maximum_grade = null;
      this.imageEntry = '';
      this.fileData = '';
      this.selected_gender = null;
      this.selected_type = null;
      this.selected_student_type = null;
      this.selected_study_mode = STUDY_MODE_OPTIONS[0];
    },
  },

  async mounted() {
    if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
    if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
    this.loadingSettings = true;
    await this.fetchFormSettings();
    this.loadingSettings = false;
  },
};
</script>
